/**
 * Copyright (c) 2015-present, Nicolas Gallagher.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 * @flow
 */

 /**
	* Updated by Javier Marquez - 2018
	*/

const emptyArray = [];
const emptyFunction = () => { };

const getRect = node => {
	if (node) {
		const isElement = node.nodeType === 1 /* Node.ELEMENT_NODE */;
		if (isElement && typeof node.getBoundingClientRect === 'function') {
			return node.getBoundingClientRect();
		}
	}
};

// Mobile Safari re-uses touch objects, so we copy the properties we want and normalize the identifier
const normalizeTouches = touches => {
	if (!touches) {
		return emptyArray;
	}

	return Array.prototype.slice.call(touches).map(touch => {
		const identifier = touch.identifier > 20 ? touch.identifier % 20 : touch.identifier;
		let rect;

		return {
			_normalized: true,
			clientX: touch.clientX,
			clientY: touch.clientY,
			force: touch.force,
			get locationX() {
				rect = rect || getRect(touch.target);
				return (rect && (touch.pageX - rect.left)) || undefined;
			},
			get locationY() {
				rect = rect || getRect(touch.target);
				return (rect && (touch.pageY - rect.top)) || undefined;
			},
			identifier: identifier,
			pageX: touch.pageX,
			pageY: touch.pageY,
			radiusX: touch.radiusX,
			radiusY: touch.radiusY,
			rotationAngle: touch.rotationAngle,
			screenX: touch.screenX,
			screenY: touch.screenY,
			target: touch.target,
			// normalize the timestamp
			// https://stackoverflow.com/questions/26177087/ios-8-mobile-safari-wrong-timestamp-on-touch-events
			timestamp: Date.now()
		};
	});
};

function normalizeTouchEvent(nativeEvent) {
	const changedTouches = normalizeTouches(nativeEvent.changedTouches);
	const touches = normalizeTouches(nativeEvent.touches);

	const preventDefault =
		typeof nativeEvent.preventDefault === 'function'
			? nativeEvent.preventDefault.bind(nativeEvent)
			: emptyFunction;
	const stopImmediatePropagation =
		typeof nativeEvent.stopImmediatePropagation === 'function'
			? nativeEvent.stopImmediatePropagation.bind(nativeEvent)
			: emptyFunction;
	const stopPropagation =
		typeof nativeEvent.stopPropagation === 'function'
			? nativeEvent.stopPropagation.bind(nativeEvent)
			: emptyFunction;

	const event = {
		_normalized: true,
		bubbles: nativeEvent.bubbles,
		cancelable: nativeEvent.cancelable,
		changedTouches,
		defaultPrevented: nativeEvent.defaultPrevented,
		identifier: undefined,
		locationX: undefined,
		locationY: undefined,
		pageX: nativeEvent.pageX,
		pageY: nativeEvent.pageY,
		preventDefault,
		stopImmediatePropagation,
		stopPropagation,
		target: nativeEvent.target,
		// normalize the timestamp
		// https://stackoverflow.com/questions/26177087/ios-8-mobile-safari-wrong-timestamp-on-touch-events
		timestamp: Date.now(),
		touches,
		type: nativeEvent.type,
		which: nativeEvent.which
	};

	if (changedTouches[0]) {
		event.identifier = changedTouches[0].identifier;
		event.pageX = changedTouches[0].pageX;
		event.pageY = changedTouches[0].pageY;
		event.locationX = changedTouches[0].locationX;
		event.locationY = changedTouches[0].locationY;
	}

	return event;
}

function normalizeMouseEvent(nativeEvent) {
	let rect;

	const touches = [
		{
			_normalized: true,
			clientX: nativeEvent.clientX,
			clientY: nativeEvent.clientY,
			force: nativeEvent.force,
			identifier: 0,
			get locationX() {
				rect = rect || getRect(nativeEvent.target);
				return (rect && (nativeEvent.pageX - rect.left)) || undefined;
			},
			get locationY() {
				rect = rect || getRect(nativeEvent.target);
				return (rect && (nativeEvent.pageY - rect.top)) || undefined;
			},
			pageX: nativeEvent.pageX,
			pageY: nativeEvent.pageY,
			screenX: nativeEvent.screenX,
			screenY: nativeEvent.screenY,
			target: nativeEvent.target,
			timestamp: Date.now()
		}
	];

	const preventDefault =
		typeof nativeEvent.preventDefault === 'function'
			? nativeEvent.preventDefault.bind(nativeEvent)
			: emptyFunction;
	const stopImmediatePropagation =
		typeof nativeEvent.stopImmediatePropagation === 'function'
			? nativeEvent.stopImmediatePropagation.bind(nativeEvent)
			: emptyFunction;
	const stopPropagation =
		typeof nativeEvent.stopPropagation === 'function'
			? nativeEvent.stopPropagation.bind(nativeEvent)
			: emptyFunction;

	return {
		_normalized: true,
		bubbles: nativeEvent.bubbles,
		cancelable: nativeEvent.cancelable,
		changedTouches: touches,
		defaultPrevented: nativeEvent.defaultPrevented,
		identifier: touches[0].identifier,
		locationX: touches[0].locationX,
		locationY: touches[0].locationY,
		pageX: nativeEvent.pageX,
		pageY: nativeEvent.pageY,
		preventDefault,
		stopImmediatePropagation,
		stopPropagation,
		target: nativeEvent.target,
		timestamp: touches[0].timestamp,
		touches: nativeEvent.type === 'mouseup' ? emptyArray : touches,
		type: nativeEvent.type,
		which: nativeEvent.which
	};
}

// TODO: how to best handle keyboard events?
function normalizeNativeEvent(nativeEvent) {
	if (!nativeEvent || nativeEvent._normalized) {
		return nativeEvent;
	}
	const eventType = nativeEvent.type || '';
	const mouse = eventType.indexOf('mouse') >= 0;
	if (mouse) {
		return normalizeMouseEvent(nativeEvent);
	} else {
		return normalizeTouchEvent(nativeEvent);
	}
}

export default normalizeNativeEvent;